import { extractErrorMessage } from "@platform/api";
import { logger } from "@platform/browser-logger";
import { useCallback } from "react";

import { useNotification } from "./useNotification";

interface ApiErrorHandlerOptions<TArgs extends unknown[] = unknown[]> {
  fallback?: string;
  onError?: (error: Error, ...args: TArgs) => void;
}

/**
 * Returns a callback that extracts the user-facing message from a backend
 * AppError and displays it as an error toast notification.
 *
 * The returned handler forwards all arguments to the optional `onError`
 * callback, preserving React Query's `(error, variables, context)` signature.
 *
 * @param options - Optional configuration
 * @param options.fallback - Fallback message when extraction fails
 * @param options.onError - Additional callback invoked after the notification is shown
 * @returns An error handler compatible with React Query's onError signature
 *
 * @example
 * ```tsx
 * const handleError = useApiErrorHandler();
 *
 * const mutation = useMutation({
 *   mutationFn: deleteThing,
 *   onError: handleError,
 * });
 * ```
 */
export const useApiErrorHandler = <TArgs extends unknown[] = unknown[]>(
  options?: ApiErrorHandlerOptions<TArgs>,
): ((error: Error, ...args: TArgs) => void) => {
  const { showError } = useNotification();
  const { fallback, onError } = options ?? {};

  return useCallback(
    (error: Error, ...args: TArgs) => {
      const message = extractErrorMessage(error, fallback);
      logger.error(message, error);
      showError(message);
      onError?.(error, ...args);
    },
    [showError, fallback, onError],
  );
};
